import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { ReportCheck, reportComparison } from '../admin';

const fmt = (v: number | null) => (v === null || v === undefined ? '—' : Number(v.toPrecision(4)).toString());

/**
 * The engine's whole-study numbers beside the vendor report's, with the tolerance verdict per metric
 * (DESIGN.md §7.9, §17.6 tier A).
 *
 * **A disagreement view, not a validation.** A report's aggregate is not ground truth in either direction —
 * so a FAIL means "the two differ by more than the stated tolerance, look here", and an OK means only that
 * they agree. Corpus reviewers only: showing these numbers to the reviewing physician would anchor them (§7.5).
 */
export const ReportComparison: React.FC<{ studyId: string }> = ({ studyId }) => {
  const [open, setOpen] = useState(false);
  const [checks, setChecks] = useState<ReportCheck[] | null>(null);
  const [note, setNote] = useState<string>('');

  useEffect(() => {
    let live = true;
    reportComparison(studyId)
      .then((r) => {
        if (!live) return;
        const scored = r.reads.filter((x) => x.checks.length > 0);
        setChecks(scored.flatMap((x) => x.checks));
        setNote(scored.length ? `report template ${scored[0].template ?? 'unknown'}` : '');
      })
      .catch(() => live && setChecks([]));
    return () => {
      live = false;
    };
  }, [studyId]);

  // Nothing to show is nothing shown: no report attached, or none scored against a run yet.
  if (!checks || checks.length === 0) return null;
  const failing = checks.filter((c) => !c.passed).length;

  return (
    <div className="glass-panel rounded-2xl p-4 text-xs">
      <button onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between">
        <span className="flex items-center gap-1.5 font-bold uppercase tracking-wide text-white">
          {open ? <ChevronDown className="h-3.5 w-3.5 text-slate-500" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-500" />}
          Engine vs vendor report
        </span>
        <span className={`rounded border px-1.5 py-0.5 text-[10px] font-semibold ${failing ? 'border-amber-800 text-amber-300' : 'border-emerald-800 text-emerald-300'}`}>
          {failing ? `${failing} of ${checks.length} differ` : 'all agree'}
        </span>
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          <p className="text-[11px] text-slate-500">
            Disagreement, not truth — a report aggregate is not ground truth in either direction. {note}
          </p>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] text-left tabular-nums">
              <thead className="text-[10px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="py-1 pr-3">Metric</th>
                  <th className="py-1 pr-3">Report</th>
                  <th className="py-1 pr-3">Engine</th>
                  <th className="py-1 pr-3">Tolerance</th>
                  <th className="py-1" />
                </tr>
              </thead>
              <tbody>
                {checks.map((c) => (
                  <tr key={c.name} className="border-t border-slate-800">
                    <td className="py-1 pr-3 text-slate-300">{c.name.replace(/_/g, ' ')}</td>
                    <td className="py-1 pr-3 text-slate-300">{fmt(c.truth)}</td>
                    <td className="py-1 pr-3 text-slate-300">{fmt(c.engine)}</td>
                    <td className="py-1 pr-3 text-slate-500">{c.reason}</td>
                    <td className={`py-1 font-semibold ${c.passed ? 'text-emerald-300' : 'text-amber-300'}`}>
                      {c.passed ? 'agree' : 'differ'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
